/**
 * Simulation Store (Zustand)
 * 시뮬레이션 관련 로컬 UI 상태 관리
 *
 * Job[] / activeJobId 제거 → simSession 단일 구조로 통합
 */

import { scenarioProgress, withSettledScenarioIndices, type ScenarioLogEvent, type ScenarioPlanItem } from '@/domain/scenario';
import { create } from 'zustand';
import type { CouplingDrivenInput } from '@/domain/cosim/couplingDrivenInputs';
import { devtools, subscribeWithSelector, persist } from 'zustand/middleware';
import { TERMINAL_SIM_STATUSES } from '@/types/simulation';
import type { PlotData, ChartLayout, ChartSize, MinorEditNamedSnapshot, ChartLineStyles, LineStyle, SimSession, ModelSimData, SimStatus, TaskMode, SimTimeWindow } from '@/types/simulation';
import type { MinorEdit } from '@/domain/mars/globals/minorEdits';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { sameContent } from '@/shared/state/settle';
import { soleParticipant } from '@/domain/cosim/coupling';

/**
 * 새로 관측된 변수 단위만 얹는다 — 기존 키는 불변(단위는 변하지 않는다).
 * prototype pollution 을 막으려 null-프로토타입을 유지하고, 바뀐 것이
 * 없으면 **원본 참조를 그대로** 돌려준다(구독자를 흔들지 않기 위해).
 */
function mergeVariableUnits(
  existing: Record<string, string> | undefined,
  incoming: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!incoming) return existing;
  const merged: Record<string, string> = Object.assign(Object.create(null), existing ?? {});
  let changed = false;
  for (const [name, unit] of Object.entries(incoming)) {
    if (unit && !Object.prototype.hasOwnProperty.call(merged, name)) {
      merged[name] = unit;
      changed = true;
    }
  }
  return changed ? merged : existing;
}

/**
 * Simulation Store 인터페이스
 */
interface SimulationStore {
  // UI 상태
  chartZoom: number;
  autoScroll: boolean;
  showDevTools: boolean;
  chartYAxisModes: Record<number, 'fixed' | 'auto'>;

  // 레이아웃 관련 상태
  chartLayouts: Record<string, ChartLayout>;
  // 카드별 선택 사이즈 — 그리드가 h를 동적 소유해 레이아웃 역산이 불가하므로
  // 명시 상태가 SSOT (#245)
  chartSizes: Record<string, ChartSize>;
  favoriteChartIds: Set<string>;
  activeTabId: string;
  autoGroupMode: boolean; // 변수 타입별 자동 그룹핑 모드
  icvActiveTabId: string; // ICV 차트 패널 독립 탭 상태
  chartCompareMode: boolean;
  compareChartIds: [string | null, string | null];

  // 라인 스타일 커스텀 상태: chartId → { dataKey → LineStyle }
  chartLineStyles: Record<string, ChartLineStyles>;

  // 시뮬레이션 세션 (단일 모델 / Co-Sim 공통)
  simSession: SimSession | null;
  activeModelId: string | null;

  // Actions: UI
  setChartZoom: (zoom: number) => void;
  setAutoScroll: (enabled: boolean) => void;
  toggleDevTools: () => void;
  setChartYAxisMode: (cardNumber: number, mode: 'fixed' | 'auto') => void;

  // Actions: 레이아웃 관리
  setChartLayout: (chartId: string, layout: ChartLayout) => void;
  setChartSize: (chartId: string, size: ChartSize) => void;
  toggleFavorite: (chartId: string) => void;
  setActiveTab: (tabId: string) => void;
  setIcvActiveTab: (tabId: string) => void;
  setAutoGroupMode: (enabled: boolean) => void;
  setCompareMode: (enabled: boolean) => void;
  addToCompare: (chartId: string, position: 0 | 1) => void;
  clearCompare: () => void;
  resetLayouts: () => void;

  // Actions: 라인 스타일
  setLineStyle: (chartId: string, dataKey: string, style: Partial<LineStyle>) => void;
  resetLineStyles: (chartId: string) => void;

  // Actions: Co-Sim 세션
  initSimSession: (
    simId: string,
    projectId: string,
    models: Array<{
      modelId: string;
      modelName: string;
      taskId: string;
      taskIndex: number;
      /** participant 종류 — 필수. 등록된 ParticipantKind여야 하며 폴백 없음 */
      kind: string;
      args: string;
      taskMode: TaskMode;
      status?: SimStatus;
    }>,
    /** 복원 경로용(#286) — 원 실행의 시작 시각을 유지해 소요 시간 계산이 어긋나지 않게.
     *  runtimeMinorEditsByModel: 이어보기가 덱에서 복원한 minor edit 정의
     *  (minorEditPlots 종별의 차트 구성·스트림 이름 → 변수명 매핑 원천) */
    options?: { startTime?: number; runtimeMinorEditsByModel?: Record<string, MinorEdit[] | null>; timeWindow?: SimTimeWindow; status?: SimStatus; demo?: boolean; scenario?: { id: string; name: string } }
  ) => void;
  /** [perf] 시작 성공 처리를 set 1회로 — 직렬 동기 렌더 6번 + 언마운트/재마운트 제거 */
  startSimSession: (
    simId: string,
    projectId: string,
    models: Array<{
      modelId: string;
      modelName: string;
      taskId: string;
      taskIndex: number;
      kind: string;
      args: string;
      taskMode: TaskMode;
      status?: SimStatus;
    }>,
    activeTabId: string,
    runtimeMinorEditsByModel?: Record<string, MinorEdit[] | null>,
    /** 이 실행의 문제 시각 구간(시작 시점 확정값) */
    timeWindow?: SimTimeWindow,
    /** 이 실행이 고른 시나리오 — 세션에 실어 실행 머리가 보인다 */
    scenario?: { id: string; name: string },
  ) => void;
  setSessionStatus: (status: SimStatus) => void;
  setActiveModel: (modelId: string | null) => void;

  // Actions: 모델 데이터
  updateModel: (modelId: string, updates: Partial<ModelSimData>) => void;
  updateModelByTaskId: (taskId: string, updates: Partial<ModelSimData>) => void;
  appendModelPlotData: (modelId: string, data: PlotData) => void;
  /** 한 flush에서 모인 스트림 산출물을 **커밋 1회**로 반영 (이어보기 백로그
   *  대응). 프레임마다 커밋하면 커밋 수만큼 차트 그리드가 재렌더된다. */
  appendModelStreamBatch: (
    modelId: string,
    batch: { plotData?: PlotData[]; screenLogs?: string[]; units?: Record<string, string>; scenarioEvents?: ScenarioLogEvent[] },
  ) => void;
  /** 백필(최근 창 조회) 산출물 반영. append 와 달리 **뒤에 잇지 않고 시간순으로
   *  끼워 넣는다** — 구독을 먼저 열고 백필을 나중에 하므로, 백필 프레임은 이미
   *  들어와 있는 라이브 프레임보다 과거다. 그대로 append 하면 차트·로그가 역순
   *  구간을 갖고, 게다가 트림이 뒤를 남기므로 최신 라이브가 잘려 나간다. */
  mergeModelStreamBacklog: (
    modelId: string,
    batch: { plotData?: PlotData[]; screenLogs?: string[]; units?: Record<string, string>; scenarioEvents?: ScenarioLogEvent[] },
  ) => void;
  /** 새로 관측된 변수 단위 병합 (기존 키는 유지 — 단위는 불변) */
  mergeModelVariableUnits: (modelId: string, units: Record<string, string>) => void;
  appendModelScreenLog: (modelId: string, log: string) => void;
  setModelLatestMinorEdit: (modelId: string, snapshot: MinorEditNamedSnapshot) => void;
  setModelRuntimeMinorEdits: (modelId: string, edits: MinorEdit[] | null) => void;
  /** 한 모델의 화면 로그만 비운다 — 실시간 로그 뷰어의 «로그 지우기». 차트·마이너 에디트는 그대로 */
  clearScreenLogs: (modelId: string) => void;
  /**
   * 세션 **모든 모델**의 스트림 적재물(plotData·screenLogs)을 비운다 —
   * 파이프라인이 같은 세션에 **다시** 붙기 직전에 부른다(파이프라인은 전
   * 모델을 한꺼번에 재구독·재백필한다). merge 는 기존 배열의 같은 프레임을
   * 걷지 못하므로, 비우지 않으면 재진입마다 포인트·로그가 중복 누적된다.
   * hadPlotData 래치는 유지한다(재주입 사이 그리드 깜빡임 방지).
   */
  resetModelStreamData: () => void;
  clearSimSession: () => void;

  /** 프로젝트 로드 시 전체 초기화 (localStorage 포함) */
  resetAll: () => void;
}

/**
 * Zustand Store 생성
 */
/** 세션 모델 입력(공통 seed) — 시작·이어보기·파이프라인 조기 시작이 공유 */
export interface SessionModelSeed {
  modelId: string;
  modelName: string;
  taskId: string;
  taskIndex: number;
  kind: string;
  args: string;
  taskMode: TaskMode;
  status?: SimStatus;
  /** 시작 시점 결선 스냅샷(#489) — 시작 경로만 싣는다 */
  couplingDrivenInputs?: CouplingDrivenInput[];
  /** 이 참가자의 시나리오 동작 계획(그 실행의 시나리오 파일에서) — 실행 머리의 진행(`scenarioProgress`) */
  scenarioPlan?: ScenarioPlanItem[];
}

/**
 * 세션·모델 맵 조립의 단일 출처.
 *
 * 종전에는 같은 모양을 initSimSession·startSimSession·useStartSimulation 의
 * pipelineSession 이 **세 벌 손으로** 만들었다 — ModelSimData 기본값이 한 곳만
 * 바뀌면 경로별 세션이 조용히 갈라진다(optional 필드는 tsc 도 못 잡는다).
 */
export function buildSimSession(
  simId: string,
  projectId: string,
  models: SessionModelSeed[],
  options?: {
    /** 복원 경로용(#286) — 원 실행 시작 시각 유지 */
    startTime?: number;
    runtimeMinorEditsByModel?: Record<string, MinorEdit[] | null>;
    /** 이 실행의 문제 시각 구간 — 시작 경로는 확정값, 이어보기는 매니페스트에서 읽은 값 */
    timeWindow?: SimTimeWindow;
    /** 실행(세션) 상태 — 주면 그것, 없으면 참가자 상태에서 끌어낸다. 이어보기는 SM 의 실행 상태를 준다
     *  (참가자 하나가 상태 모름이어도 실행 상태가 그것을 따라가지 않게) */
    status?: SimStatus;
    /** 데모 세션(`SimSession.demo`) — 서버에 실행이 없다 */
    demo?: boolean;
    /** 이 실행이 고른 시나리오 — 시작 경로는 붙인 결과, 이어보기는 매니페스트 */
    scenario?: { id: string; name: string };
  },
): SimSession {
  const now = options?.startTime ?? Date.now();
  const modelMap: Record<string, ModelSimData> = {};
  let initialStatus: SimStatus = 'building';
  for (const m of models) {
    modelMap[m.modelId] = {
      ...m,
      status: m.status ?? 'building',
      startTime: now,
      plotData: [],
      screenLogs: [],
      scenarioEvents: [],
      latestMinorEdit: null,
      runtimeMinorEdits: options?.runtimeMinorEditsByModel?.[m.modelId] ?? null,
    };
    if (initialStatus === 'building' && m.status) {
      initialStatus = m.status;
    }
  }
  return {
    simId, projectId, status: options?.status ?? initialStatus, startTime: now, timeWindow: options?.timeWindow, models: modelMap,
    ...(options?.scenario ? { scenario: options.scenario } : {}),
    ...(options?.demo ? { demo: true as const } : {}),
  };
}

export const useSimulationStore = create<SimulationStore>()(
  devtools(
    persist(
      subscribeWithSelector((set, _get) => ({
        // 초기 상태
        chartZoom: 1,
        autoScroll: true,
        showDevTools: false,
        chartYAxisModes: {},
        chartLayouts: {},
        chartSizes: {},
        favoriteChartIds: new Set<string>(),
        activeTabId: 'all',
        icvActiveTabId: 'all',
        autoGroupMode: false,
        chartCompareMode: false,
        compareChartIds: [null, null],
        chartLineStyles: {},
        simSession: null,
        activeModelId: null,

        // UI 액션
        setChartZoom: (zoom) =>
          set({ chartZoom: zoom }, false, 'setChartZoom'),

        setAutoScroll: (enabled) =>
          set({ autoScroll: enabled }, false, 'setAutoScroll'),

        toggleDevTools: () =>
          set((state) => ({ showDevTools: !state.showDevTools }), false, 'toggleDevTools'),

        setChartYAxisMode: (cardNumber, mode) =>
          set((state) => ({
            chartYAxisModes: {
              ...state.chartYAxisModes,
              [cardNumber]: mode,
            },
          }), false, 'setChartYAxisMode'),

        // 레이아웃 관리 액션
        setChartLayout: (chartId, layout) =>
          set((state) => ({
            chartLayouts: {
              ...state.chartLayouts,
              [chartId]: layout,
            },
          }), false, 'setChartLayout'),

        setChartSize: (chartId, size) =>
          set((state) => ({
            chartSizes: {
              ...state.chartSizes,
              [chartId]: size,
            },
          }), false, 'setChartSize'),

        toggleFavorite: (chartId) =>
          set((state) => {
            const newFavorites = new Set(state.favoriteChartIds);
            if (newFavorites.has(chartId)) {
              newFavorites.delete(chartId);
            } else {
              newFavorites.add(chartId);
            }
            return { favoriteChartIds: newFavorites };
          }, false, 'toggleFavorite'),

        setActiveTab: (tabId) =>
          set({ activeTabId: tabId }, false, 'setActiveTab'),

        setIcvActiveTab: (tabId) =>
          set({ icvActiveTabId: tabId }, false, 'setIcvActiveTab'),

        setAutoGroupMode: (enabled) =>
          set({ autoGroupMode: enabled, activeTabId: 'all', icvActiveTabId: 'all' }, false, 'setAutoGroupMode'),

        setCompareMode: (enabled) =>
          set({ chartCompareMode: enabled }, false, 'setCompareMode'),

        addToCompare: (chartId, position) =>
          set((state) => {
            const newCompareIds: [string | null, string | null] = [...state.compareChartIds];
            newCompareIds[position] = chartId;
            return { compareChartIds: newCompareIds };
          }, false, 'addToCompare'),

        clearCompare: () =>
          set({ compareChartIds: [null, null], chartCompareMode: false }, false, 'clearCompare'),

        resetLayouts: () =>
          // 사이즈는 레이아웃과 한 몸 — 배치만 리셋되고 라벨/토글이 옛 선택을
          // 기억하면 불일치가 남는다 (#245 리뷰 반영)
          set({ chartLayouts: {}, chartSizes: {} }, false, 'resetLayouts'),

        setLineStyle: (chartId, dataKey, style) =>
          set((state) => ({
            chartLineStyles: {
              ...state.chartLineStyles,
              [chartId]: {
                ...state.chartLineStyles[chartId],
                [dataKey]: {
                  ...state.chartLineStyles[chartId]?.[dataKey],
                  ...style,
                },
              },
            },
          }), false, 'setLineStyle'),

        resetLineStyles: (chartId) =>
          set((state) => {
            const { [chartId]: _, ...rest } = state.chartLineStyles;
            return { chartLineStyles: rest };
          }, false, 'resetLineStyles'),

        // 세션 관리 액션 (단일 모델 / Co-Sim 공통)
        initSimSession: (simId, projectId, models, options) =>
          set(() => ({
            simSession: buildSimSession(simId, projectId, models, options),
            // 단일 모델이면 자동으로 activeModelId 설정
            activeModelId: soleParticipant(models)?.modelId ?? null,
          }), false, 'initSimSession'),

        /**
         * [perf] 시작 성공 처리를 **한 번의 set 으로** 끝낸다.
         *
         * 종전에는 onSuccess 가 clearSimSession → setActiveTab → initSimSession →
         * setActiveModel → setModelRuntimeMinorEdits(모델 수만큼) 를 순서대로 불렀다.
         * zustand + useSyncExternalStore 조합은 set 마다 SyncLane 을 강제하므로
         * 이게 **직렬 동기 렌더 6번**이 됐고, 그 사이 메인 스레드가 통째로 막혀
         * 구독(GRF/Subscribe) 요청이 나가지 못했다 — Build 응답 → 구독 요청 8초 공백의 정체.
         *
         * 더 나쁜 건 첫 줄이었다: clearSimSession 이 simSession 을 null 로 만들어
         * 시뮬레이션 뷰 전체를 **언마운트**시키고, 세 줄 뒤 initSimSession 이 다시
         * **마운트**했다. 재렌더가 아니라 마운트라 memo 로는 막을 수 없었다.
         * 여기서는 null 을 거치지 않고 원자적으로 교체한다.
         */
        startSimSession: (simId, projectId, models, activeTabId, runtimeMinorEditsByModel, timeWindow, scenario) =>
          set(() => ({
            activeTabId,
            simSession: buildSimSession(simId, projectId, models, { runtimeMinorEditsByModel, timeWindow, scenario }),
            // 단일 모델이면 자동 선택, co-sim 은 null — "All" 탭(참가자별 섹션)이
            // 기본 뷰다. 종전 initSimSession 의 규칙을 그대로 지킨다.
            activeModelId: soleParticipant(models)?.modelId ?? null,
          }), false, 'startSimSession'),

        setSessionStatus: (status) =>
          set((state) => {
            if (!state.simSession) return {};
            // 종결(completed/stopped/failed)은 세션 사실 — 종료 응답에 빠진 태스크도 이 시점에 끝났다.
            // 모델별 endTime 을 여기서 채워야 세션 시계(가장 먼저 시작 ~ 마지막 종료)가 0s 로 무너지지 않는다.
            const terminal = TERMINAL_SIM_STATUSES.includes(status);
            const models = terminal
              ? Object.fromEntries(
                  Object.entries(state.simSession.models).map(([id, m]) => [id, m.endTime == null ? { ...m, endTime: Date.now() } : m]),
                )
              : state.simSession.models;
            return {
              simSession: { ...state.simSession, status, models },
            };
          }, false, 'setSessionStatus'),

        setActiveModel: (modelId) =>
          set({ activeModelId: modelId }, false, 'setActiveModel'),

        // 모델 데이터 액션 — 세션 status 는 다시 계산하지 않는다(세션 상태는 서버 사건·재조정·`setSessionStatus` 가 정한다)
        updateModel: (modelId, updates) =>
          set((state) => {
            if (!state.simSession?.models[modelId]) return {};
            const model = state.simSession.models[modelId];
            const newModels = {
              ...state.simSession.models,
              [modelId]: { ...model, ...updates },
            };
            return {
              simSession: {
                ...state.simSession,
                models: newModels,
              },
            };
          }, false, 'updateModel'),

        updateModelByTaskId: (taskId, updates) =>
          set((state) => {
            if (!state.simSession) return {};
            const models = state.simSession.models;
            const entry = Object.entries(models).find(([, m]) => m.taskId === taskId);
            if (!entry) return {};
            const [modelId, model] = entry;
            const newModels = {
              ...models,
              [modelId]: { ...model, ...updates },
            };
            return {
              simSession: {
                ...state.simSession,
                models: newModels,
              },
            };
          }, false, 'updateModelByTaskId'),

        mergeModelVariableUnits: (modelId, units) =>
          set((state) => {
            const model = state.simSession?.models[modelId];
            if (!model) return state;
            const existing = model.variableUnits ?? {};
            let changed = false;
            // null-프로토타입 + 자기 소유 키 검사 — 외부 입력 키의
            // prototype pollution과 프로토타입 체인 오검출 방지
            const merged: Record<string, string> = Object.assign(Object.create(null), existing);
            for (const [name, unit] of Object.entries(units)) {
              if (unit && !Object.prototype.hasOwnProperty.call(merged, name)) {
                merged[name] = unit;
                changed = true;
              }
            }
            if (!changed) return state;
            return {
              simSession: {
                ...state.simSession!,
                models: {
                  ...state.simSession!.models,
                  [modelId]: { ...model, variableUnits: merged },
                },
              },
            };
          }, false, 'mergeModelVariableUnits'),
        appendModelPlotData: (modelId, data) =>
          set((state) => {
            if (!state.simSession?.models[modelId]) return {};
            const model = state.simSession.models[modelId];
            const newData = [...model.plotData, data];
            const trimmed = newData.length > 3000 ? newData.slice(-3000) : newData;
            return {
              simSession: {
                ...state.simSession,
                models: {
                  ...state.simSession.models,
                  [modelId]: { ...model, plotData: trimmed },
                },
              },
            };
          }, false, 'appendModelPlotData'),

        appendModelStreamBatch: (modelId, batch) =>
          set((state) => {
            const model = state.simSession?.models[modelId];
            // 진짜 no-op은 state를 그대로 돌려준다 — {}를 주면 zustand가 새 state
            // 객체를 만들어 구독자를 흔든다(mergeModelVariableUnits와 같은 규칙).
            if (!model) return state;
            const { plotData = [], screenLogs = [], units, scenarioEvents = [] } = batch;

            let nextPlotData = model.plotData;
            if (plotData.length > 0) {
              const merged = [...model.plotData, ...plotData];
              nextPlotData = merged.length > 3000 ? merged.slice(-3000) : merged;
            }

            let nextScreenLogs = model.screenLogs;
            if (screenLogs.length > 0) {
              const merged = [...model.screenLogs, ...screenLogs];
              nextScreenLogs = merged.length > 500 ? merged.slice(-500) : merged;
            }

            // 시나리오 사건은 실행당 동작 수의 몇 배라 상한을 두지 않는다(차트 표식이 지워지면 안 된다)
            const nextScenarioEvents = scenarioEvents.length > 0 ? [...(model.scenarioEvents ?? []), ...scenarioEvents] : model.scenarioEvents;
            const nextScenarioSettled = withSettledScenarioIndices(model.scenarioSettled, scenarioEvents);

            const nextUnits = mergeVariableUnits(model.variableUnits, units);

            if (
              nextPlotData === model.plotData &&
              nextScreenLogs === model.screenLogs &&
              nextScenarioEvents === model.scenarioEvents &&
              nextScenarioSettled === model.scenarioSettled &&
              nextUnits === model.variableUnits
            ) {
              return state;
            }

            return {
              simSession: {
                ...state.simSession!,
                models: {
                  ...state.simSession!.models,
                  [modelId]: {
                    ...model,
                    plotData: nextPlotData,
                    // 차트 마운트 래치 — 재진입 비움(resetModelStreamData)으로 배열이 비어도 유지
                    hadPlotData: model.hadPlotData || plotData.length > 0,
                    screenLogs: nextScreenLogs,
                    scenarioEvents: nextScenarioEvents,
                    ...(nextScenarioSettled ? { scenarioSettled: nextScenarioSettled } : {}),
                    variableUnits: nextUnits,
                  },
                },
              },
            };
          }, false, 'appendModelStreamBatch'),

        mergeModelStreamBacklog: (modelId, batch) =>
          set((state) => {
            const model = state.simSession?.models[modelId];
            if (!model) return state;
            const { plotData = [], screenLogs = [], units, scenarioEvents = [] } = batch;
            // 시나리오 사건 — 백필은 라이브보다 과거라 앞에 붙이고 sim 시각 순으로 둔다(시각 없는 사건은 뒤)
            const nextScenarioEvents = scenarioEvents.length > 0
              ? [...scenarioEvents, ...(model.scenarioEvents ?? [])].sort((a, b) => {
                  const at = (e: ScenarioLogEvent) => e.t ?? Infinity;
                  return at(a) === at(b) ? 0 : at(a) - at(b); // 둘 다 시각이 없으면(Infinity − Infinity = NaN) 같은 자리
                })
              : model.scenarioEvents;
            const nextScenarioSettled = withSettledScenarioIndices(model.scenarioSettled, scenarioEvents);

            // 플롯은 time 을 키로 진짜 병합한다. 백필분이 전부 과거라는 보장에
            // 기대지 않는다 — 겹침 제거는 FrameDedup 이 이미 했고, 여기서는
            // 순서만 책임진다.
            let nextPlotData = model.plotData;
            if (plotData.length > 0) {
              const incoming = [...plotData].sort((a, b) => a.time - b.time);
              const existing = model.plotData;
              const merged: PlotData[] = [];
              let i = 0;
              let j = 0;
              while (i < existing.length && j < incoming.length) {
                merged.push(existing[i].time <= incoming[j].time ? existing[i++] : incoming[j++]);
              }
              while (i < existing.length) merged.push(existing[i++]);
              while (j < incoming.length) merged.push(incoming[j++]);
              nextPlotData = merged.length > 3000 ? merged.slice(-3000) : merged;
            }

            // 로그 줄은 정렬 키가 없다(자유 문자열). 백필분은 구독 이전 구간이라
            // 앞에 붙이는 것이 곧 시간순이다.
            let nextScreenLogs = model.screenLogs;
            if (screenLogs.length > 0) {
              const merged = [...screenLogs, ...model.screenLogs];
              nextScreenLogs = merged.length > 500 ? merged.slice(-500) : merged;
            }

            const nextUnits = mergeVariableUnits(model.variableUnits, units);

            if (
              nextPlotData === model.plotData &&
              nextScreenLogs === model.screenLogs &&
              nextScenarioEvents === model.scenarioEvents &&
              nextScenarioSettled === model.scenarioSettled &&
              nextUnits === model.variableUnits
            ) {
              return state;
            }

            return {
              simSession: {
                ...state.simSession!,
                models: {
                  ...state.simSession!.models,
                  [modelId]: {
                    ...model,
                    plotData: nextPlotData,
                    // 차트 마운트 래치 — appendModelStreamBatch 와 같은 규칙
                    hadPlotData: model.hadPlotData || plotData.length > 0,
                    screenLogs: nextScreenLogs,
                    scenarioEvents: nextScenarioEvents,
                    ...(nextScenarioSettled ? { scenarioSettled: nextScenarioSettled } : {}),
                    variableUnits: nextUnits,
                  },
                },
              },
            };
          }, false, 'mergeModelStreamBacklog'),

        appendModelScreenLog: (modelId, log) =>
          set((state) => {
            if (!state.simSession?.models[modelId]) return {};
            const model = state.simSession.models[modelId];
            const newLogs = [...model.screenLogs, log];
            const trimmed = newLogs.length > 500 ? newLogs.slice(-500) : newLogs;
            return {
              simSession: {
                ...state.simSession,
                models: {
                  ...state.simSession.models,
                  [modelId]: { ...model, screenLogs: trimmed },
                },
              },
            };
          }, false, 'appendModelScreenLog'),

        setModelLatestMinorEdit: (modelId, snapshot) =>
          set((state) => {
            if (!state.simSession?.models[modelId]) return {};
            const model = state.simSession.models[modelId];
            return {
              simSession: {
                ...state.simSession,
                models: {
                  ...state.simSession.models,
                  [modelId]: { ...model, latestMinorEdit: snapshot },
                },
              },
            };
          }, false, 'setModelLatestMinorEdit'),

        setModelRuntimeMinorEdits: (modelId, edits) =>
          set((state) => {
            if (!state.simSession?.models[modelId]) return {};
            const model = state.simSession.models[modelId];
            return {
              simSession: {
                ...state.simSession,
                models: {
                  ...state.simSession.models,
                  [modelId]: { ...model, runtimeMinorEdits: edits },
                },
              },
            };
          }, false, 'setModelRuntimeMinorEdits'),

        resetModelStreamData: () =>
          set((state) => {
            if (!state.simSession) return state;
            const models = { ...state.simSession.models };
            for (const id of Object.keys(models)) {
              models[id] = { ...models[id], plotData: [], screenLogs: [], scenarioEvents: [] };
            }
            return { simSession: { ...state.simSession, models } };
          }, false, 'resetModelStreamData'),

        clearScreenLogs: (modelId) =>
          set((state) => {
            const model = state.simSession?.models[modelId];
            if (!state.simSession || !model) return {};
            return {
              simSession: {
                ...state.simSession,
                models: { ...state.simSession.models, [modelId]: { ...model, screenLogs: [] } },
              },
            };
          }, false, 'clearScreenLogs'),

        clearSimSession: () =>
          set({
            simSession: null,
            activeModelId: null,
          }, false, 'clearSimSession'),

        resetAll: () =>
          set({
            chartZoom: 1,
            autoScroll: true,
            showDevTools: false,
            chartYAxisModes: {},
            chartLayouts: {},
            chartSizes: {},
            favoriteChartIds: new Set<string>(),
            activeTabId: 'all',
            autoGroupMode: false,
            chartCompareMode: false,
            compareChartIds: [null, null],
            chartLineStyles: {},
            simSession: null,
            activeModelId: null,
          }, false, 'resetAll'),
      })),
      {
        name: 'simulation-layout-storage',
        partialize: (state) => ({
          chartLayouts: state.chartLayouts,
          chartSizes: state.chartSizes,
          favoriteChartIds: Array.from(state.favoriteChartIds),
          activeTabId: state.activeTabId,
          icvActiveTabId: state.icvActiveTabId,
        }),
        merge: (persistedState: unknown, currentState: SimulationStore) => {
          const persisted = (persistedState ?? {}) as Partial<SimulationStore> & {
            favoriteChartIds?: Iterable<string>;
          };
          return {
            ...currentState,
            ...persisted,
            favoriteChartIds: persisted.favoriteChartIds
              ? new Set(persisted.favoriteChartIds)
              : currentState.favoriteChartIds,
          };
        },
      }
    ),
    {
      name: 'simulation-store',
      enabled: import.meta.env.DEV,
    }
  )
);

// DEV: 브라우저 콘솔에서 store 접근 가능하게 노출
if (import.meta.env.DEV) {
  (window as { __simStore?: unknown }).__simStore = useSimulationStore;
}

/** 시뮬레이션 데이터 존재 여부 (팝업 판단용) */
export const hasSimulationData = () => {
  const session = useSimulationStore.getState().simSession;
  if (!session) return false;
  return Object.values(session.models).some(
    (m) => m.plotData.length > 0 || m.status === 'running'
  );
};

/**
 * 선택적 구독 훅들 (성능 최적화)
 */

const EMPTY_PLOT_DATA: PlotData[] = [];
const EMPTY_SCREEN_LOGS: string[] = [];

// 활성 모델 전체 데이터 (기존 useActiveJob 대체)
/**
 * 활성 모델의 구조(상태·taskId·kind·시각…) — 라이브 필드(LIVE_MODEL_FIELDS)가 배치마다 바뀌어도 같은 참조.
 * 소비자 전수(로그 뷰어·ICV 패널)는 구조 필드만 읽는다. 라이브 값은 useScreenLogs·usePlotData 같은 잎 훅으로.
 */
export const useActiveModel = (): StructuralModel | null =>
  useStoreWithEqualityFn(useSimulationStore, selectActiveStructuralModel, sameContent);

// 활성 모델의 taskId (MARS 서비스용)
export const getActiveTaskId = () => {
  const { simSession, activeModelId } = useSimulationStore.getState();
  if (!simSession) return null;
  const targetId = activeModelId ?? Object.keys(simSession.models)[0];
  return simSession.models[targetId]?.taskId ?? null;
};

// 전체 모델 목록
export const useSessionModels = () =>
  useSimulationStore((state) =>
    state.simSession ? Object.values(state.simSession.models) : []
  );

// 플롯 데이터만 구독 (activeModelId 또는 overrideModelId 기반)
export const useModelVariableUnits = (overrideModelId?: string) =>
  useSimulationStore((state) => {
    const { simSession, activeModelId } = state;
    const targetId = overrideModelId ?? activeModelId;
    return (simSession && targetId && simSession.models[targetId]?.variableUnits) || undefined;
  });

/** 플롯 데이터의 변수 키 집합(정렬, time 제외·숫자만) — 포인트가 늘어도 키가 같으면 같은 참조. 그리드는 이것만 구독한다(트랙 B-2) */
const EMPTY_KEYS: string[] = [];
const selectPlotVariableKeys = (targetId: string | null | undefined) => (state: SimulationStore): string[] => {
  const model = targetId ? state.simSession?.models[targetId] : undefined;
  if (!model || model.plotData.length === 0) return EMPTY_KEYS;
  const keys = new Set<string>();
  for (const point of model.plotData) for (const key of Object.keys(point)) if (key !== 'time' && Number.isFinite(point[key])) keys.add(key);
  return Array.from(keys).sort();
};
export const usePlotVariableKeys = (overrideModelId?: string): string[] => {
  const activeModelId = useSimulationStore((state) => state.activeModelId);
  return useStoreWithEqualityFn(useSimulationStore, selectPlotVariableKeys(overrideModelId ?? activeModelId), sameContent);
};

export const usePlotData = (overrideModelId?: string) =>
  useSimulationStore((state) => {
    const { simSession, activeModelId } = state;
    const targetId = overrideModelId ?? activeModelId;
    if (simSession && targetId && simSession.models[targetId]) {
      return simSession.models[targetId].plotData;
    }
    return EMPTY_PLOT_DATA;
  });

// 시나리오 사건만 구독 (activeModelId 또는 overrideModelId 기반) — 차트 표식의 원천. 참가자 탭의 그리드는 modelId 없이
// 활성 모델을 그리므로 표식도 같은 규칙으로 고른다(usePlotData 와 같은 모델)
const EMPTY_SCENARIO_EVENTS: ScenarioLogEvent[] = [];
export const useScenarioEvents = (overrideModelId?: string) =>
  useSimulationStore((state) => {
    const { simSession, activeModelId } = state;
    const targetId = overrideModelId ?? activeModelId;
    return (simSession && targetId && simSession.models[targetId]?.scenarioEvents) || EMPTY_SCENARIO_EVENTS;
  });

/** 그 실행의 시나리오 진행(결과가 난 동작 / 전체) — 세션이 그 실행일 때만. 참가자마다 계획·결과가 난 번호·sim 시각을 모아 `scenarioProgress` 에 */
const sameProgress = (a: { done: number; total: number } | undefined, b: { done: number; total: number } | undefined) =>
  a === b || (!!a && !!b && a.done === b.done && a.total === b.total);
export const useScenarioProgress = (simId: string | undefined) =>
  useStoreWithEqualityFn(
    useSimulationStore,
    (state) => {
      const session = state.simSession;
      if (!session || !simId || session.simId !== simId) return undefined;
      return scenarioProgress(
        Object.values(session.models).map((m) => ({
          plan: m.scenarioPlan,
          settled: m.scenarioSettled,
          simTime: m.lastSimState ? m.lastSimState.sim_time_ms / 1000 : undefined,
        })),
      );
    },
    sameProgress,
  );

// 스크린 로그만 구독 (activeModelId 또는 overrideModelId 기반)
export const useScreenLogs = (overrideModelId?: string) =>
  useSimulationStore((state) => {
    const { simSession, activeModelId } = state;
    const targetId = overrideModelId ?? activeModelId;
    if (simSession && targetId && simSession.models[targetId]) {
      return simSession.models[targetId].screenLogs;
    }
    return EMPTY_SCREEN_LOGS;
  });

// 최신 MinorEdit 스냅샷만 구독 (activeModelId 또는 overrideModelId 기반)
export const useLatestMinorEdit = (overrideModelId?: string) =>
  useSimulationStore((state) => {
    const { simSession, activeModelId } = state;
    const targetId = overrideModelId ?? activeModelId;
    if (simSession && targetId && simSession.models[targetId]) {
      return simSession.models[targetId].latestMinorEdit;
    }
    return null;
  });

// runtimeMinorEdits 구독 (overrideModelId 지원)
export const useRuntimeMinorEdits = (overrideModelId?: string) =>
  useSimulationStore((state) => {
    const { simSession, activeModelId } = state;
    const targetId = overrideModelId ?? activeModelId;
    if (simSession && targetId && simSession.models[targetId]) {
      return simSession.models[targetId].runtimeMinorEdits;
    }
    return null;
  });

// Co-Sim 세션 구독
/**
 * [perf] 차트를 그릴 데이터가 하나라도 들어왔는가 — **스칼라** 구독.
 *
 * 시작 직후 차트 그리드를 미리 마운트하면 그 마운트(fiber 1,500~3,000 / 깊이 110~130)가
 * 메인 스레드를 8초 잡고, 그동안 백필 응답조차 읽지 못한다(2026-08-10 실측).
 * 데이터가 없으면 차트는 어차피 그릴 게 없으므로, 첫 데이터 이후로 마운트를 미룬다.
 * 불리언이라 true 로 바뀌는 한 번만 재렌더를 유발한다.
 *
 * **래치다** — "지금 있는가"가 아니라 모델의 hadPlotData(한 번이라도 받았는가)를
 * 본다. 현재값으로 게이트하면 재진입 비움(resetModelStreamData)이 plotData 를 비우는
 * 순간 그리드 전체가 언마운트되고, 완료된 세션에서는 영구히 사라진다.
 *
 * **모델 단위다** — modelId 를 주면 그 모델만 본다. 세션 아무 모델의 데이터로
 * 게이트하면 co-sim 에서 첫 참가자의 포인트 하나가 모든 참가자의 그리드를
 * 동시에 마운트해, 지연 마운트가 피하려던 첫 마운트 정지를 그대로 되살린다.
 * modelId 생략 = 세션 전체(단일 모델 뷰에서 활성 모델이 없을 때의 예비 경로).
 */
export const useHasPlotData = (modelId?: string | null) =>
  useSimulationStore((state) => {
    const models = state.simSession?.models;
    if (!models) return false;
    if (modelId) {
      const m = models[modelId];
      return !!m && (m.hadPlotData === true || m.plotData.length > 0);
    }
    for (const key in models) {
      if (models[key].hadPlotData === true || models[key].plotData.length > 0) return true;
    }
    return false;
  });

/**
 * 라이브 필드 — 스트림 배치·진행 프레임마다 바뀌는 모델 필드(트랙 B-2, 선언 한 곳).
 * 페이지·패널은 세션의 **구조**(아래 useSimSession)만 구독하고, 이 필드는 잎(차트·로그·판독부)이 자기 셀렉터로 구독한다.
 * 실측(계획서 §5): 페이지 키에 진행 수치가 들어 있을 때 프레임마다 페이지 전체(2,200 fiber)가 1.5~3.6초씩 다시 그려졌다.
 */
export const LIVE_MODEL_FIELDS = ['plotData', 'screenLogs', 'scenarioEvents', 'scenarioSettled', 'variableUnits', 'hadPlotData', 'lastSimState', 'latestMinorEdit'] as const satisfies readonly (keyof ModelSimData)[];
export type LiveModelField = (typeof LIVE_MODEL_FIELDS)[number];
export type StructuralModel = Omit<ModelSimData, LiveModelField>;
export type StructuralSession = Omit<SimSession, 'models'> & { models: Record<string, StructuralModel> };

function stripLiveFields(model: ModelSimData): StructuralModel {
  const rest: Record<string, unknown> = { ...model };
  for (const f of LIVE_MODEL_FIELDS) delete rest[f];
  return rest as StructuralModel;
}
const selectActiveStructuralModel = (state: SimulationStore): StructuralModel | null => {
  const { simSession, activeModelId } = state;
  const m = simSession && activeModelId ? simSession.models[activeModelId] : undefined;
  return m ? stripLiveFields(m) : null;
};
const selectStructuralSession = (state: SimulationStore): StructuralSession | null => {
  const ss = state.simSession;
  if (!ss) return null;
  const models: Record<string, StructuralModel> = {};
  for (const [id, m] of Object.entries(ss.models)) models[id] = stripLiveFields(m);
  return { ...ss, models };
};

/** 세션의 구조(참가자·상태·시각) — 라이브 필드가 바뀌어도 같은 참조를 돌려준다(내용 동등이면 이전 값 유지) */
export const useSimSession = (): StructuralSession | null =>
  useStoreWithEqualityFn(useSimulationStore, selectStructuralSession, sameContent);

/** 라이브 판독용 — 배치마다 새 참조. 판독부(컨트롤 바) 같은 잎에서만 쓴다 */
export const useLiveSessionModels = () =>
  useSimulationStore((state) => (state.simSession ? Object.values(state.simSession.models) : EMPTY_MODELS));
const EMPTY_MODELS: ModelSimData[] = [];

// 활성 모델 ID 구독
export const useActiveModelId = () =>
  useSimulationStore((state) => state.activeModelId);

// UI 상태만 구독
export const useSimulationUI = () =>
  useSimulationStore((state) => ({
    chartZoom: state.chartZoom,
    autoScroll: state.autoScroll,
    showDevTools: state.showDevTools,
  }));
